import { ChangeDetectorRef, Component, inject, OnInit, ViewEncapsulation } from '@angular/core'
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { LoggerService } from '@danangelus/ngx-app-core'
import { ThreadUtils } from "@danangelus/ngx-utils";
import { ConfirmationService, TreeNode } from 'primeng/api'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { DividerModule } from 'primeng/divider'
import { FloatLabelModule } from 'primeng/floatlabel'
import { InputTextModule } from 'primeng/inputtext'
import { KeyFilterModule } from 'primeng/keyfilter'
import { MessageModule } from 'primeng/message'
import { PaginatorModule } from 'primeng/paginator'
import { PickListModule } from 'primeng/picklist'
import { ToggleSwitchModule } from 'primeng/toggleswitch'
import { AppManagerService } from '../../../../core/services/app-manager.service'
import { LdapManagerService } from '../../../../core/services/ldap-manager.service'
import { ValidationUtils } from '../../../../core/utils/validation.utils'
import { GatewayError } from '../../../../models/gateway.model'
import { Group } from '../../../../models/group.model'
import { Page } from '../../../../models/page.model'
import { PermissionGroup } from '../../../../models/permission-group.model'

const log = LoggerService.get('EditPageComponent')

@Component({
    selector: 'edit-page',
    standalone: true,
    imports: [
        // Angular
        ReactiveFormsModule,
        // PrimeNG
        ConfirmDialogModule,
        DividerModule,
        FloatLabelModule,
        InputTextModule,
        KeyFilterModule,
        MessageModule,
        PaginatorModule,
        PickListModule,
        ToggleSwitchModule,
    ],
    providers: [ConfirmationService],
    templateUrl: './edit-page.component.html',
    styleUrl: './edit-page.component.sass',
    encapsulation: ViewEncapsulation.None
})
export class EditPageComponent implements OnInit {

    router = inject(Router)
    route = inject(ActivatedRoute)
    changeDetector = inject(ChangeDetectorRef)
    confirmationService = inject(ConfirmationService)
    appManagerService = inject(AppManagerService)
    ldapService: LdapManagerService = inject(LdapManagerService)

    // @ts-expect-error Assigned in ngOnInit
    form: UntypedFormGroup

    editing = false
    messageType = ''

    id = ''
    parentApplicationId: string | undefined
    parentPageId: string | undefined
    applicationPageTree: TreeNode[] = []
    page: Page | undefined

    groups: Group[] = []
    availableGroups: Group[] = []
    assignedGroups: Group[] = []

    async ngOnInit() {
        this.route.params.subscribe(params => {
            this.id = params['pageId']
            this.parentApplicationId = params['applicationId']
            this.route.queryParams.subscribe(params => {
                this.parentPageId = params['parentPageId']
                this.init().then(() => log.debug('ngOnInit', `Page [${this.page?.id}] has been loaded`))
            })
        })
    }

    private async init() {
        if (this.id) {
            // Pause ensures that the page is loaded before the form is initialised
            await ThreadUtils.sleep(200)
            this.page = await this.appManagerService.loadPage(this.id!)
            this.editing = true
            this.parentPageId = this.page!.parentPageId
        } else {
            this.page = new Page()
        }

        this.groups = await this.ldapService.getAllGroups()

        this.groups.forEach(group => {
            const isAssigned = this.page?.groups.some(
                (permissionGroup => permissionGroup.name === group.groupId)
            )
            if (isAssigned) {
                this.assignedGroups.push(group)
            } else {
                this.availableGroups.push(group)
            }
        })
        this.sortGroups(this.availableGroups)
        this.sortGroups(this.assignedGroups)

        this.form = new UntypedFormGroup({
            description: new UntypedFormControl(this.page?.description, []),
            title: new UntypedFormControl(this.page?.title, []),
            displayOrder: new UntypedFormControl(this.page?.displayOrder, Validators.required),
            expertCode: new UntypedFormControl(this.page?.expertCode, Validators.required),
            groups: new UntypedFormControl(this.page?.groups, []),
            locked: new UntypedFormControl(this.page?.locked, []),
            name: new UntypedFormControl(this.page?.name, Validators.required),
            pageUrl: new UntypedFormControl(this.page?.pageUrl, Validators.required)
        })

        this.changeDetector.detectChanges()
    }

    /**
     * A handler for the save button's click event.
     *
     * This will send a PUT request via {@linkcode AppManagerService#saveApplication} when saving an existing Application
     * Or it will send a POST request via {@linkcode AppManagerService#createApplication} when creating a new Application
     */
    async save(): Promise<void> {
        await this.validateAll()
        if (this.form.invalid) {
            this.messageType = 'FORM_INVALID'
            return
        }

        try {
            this.page = new Page(this.form.value)
            this.page.groups = this.assignedGroups.map(group => new PermissionGroup(group.groupId, group.dn))
            this.page.parentPageId = this.parentPageId

            let response: any
            if (this.id) {
                this.page!.id = this.id
                response = await this.appManagerService.savePage(this.page!, this.parentApplicationId!)
            } else {
                response = await this.appManagerService.createPage(this.page!, this.parentApplicationId!)
            }

            if (response === true) {
                log.debug('save', `Page [${this.page!.id}] has been updated`)
                this.messageType = 'CHANGES_SAVED'
                await this.cancel()
            } else if (response instanceof GatewayError) {
                switch (response.error) {
                    case 'NOT_UNIQUE':
                        this.messageType = response.error
                        this.form.controls['expertCode'].setErrors({ duplicate: true })
                        break
                    default:
                        this.messageType = 'ERROR_SAVING'
                }
            }
        } catch (err: any) {
            log.error(`Error when ${this.editing ? 'updating' : 'creating'} Page ${this.page!.id} ${err}`)
        }
    }

    /**
     * A handler for the delete button's click event.
     *
     * This will send a delete request via {@linkcode AppManagerService#deleteApplication}
     * to remove the current Application from the database
     */
    async delete(event: Event): Promise<void> {
        log.info('deleteApplication', `Attempting to delete page.`)
        event.preventDefault()
        let removingChildren = false
        const pageTreeNode = this.findNodeById(this.applicationPageTree, this.page?.id)
        if (pageTreeNode?.children?.length) {
            removingChildren = true
        }
        this.confirmationService.confirm({
            message: removingChildren ? `Are you sure you want to delete ${this.page?.name} and all its children?` : `Are you sure you want to delete ${this.page?.name}?`,
            header: `Delete ${this.page?.name}?`,
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                log.debug('delete', `Deleting Page ${this.page?.name}`)
                const response = await this.appManagerService.deletePage(this.page!.id!)
                if (response instanceof GatewayError) {
                    switch (response.error) {
                        case 'NOT_EXISTS':
                        case 'IN_USE':
                            this.messageType = response.error
                            break
                        default:
                            this.messageType = 'ERROR_SAVING'
                    }
                } else {
                    await this.cancel()
                }
            },
            reject: () => {
                this.messageType = 'DELETION_CANCELLED'
            }
        })
    }

    /**
     * Handler for the cancel button's click event.
     *
     * This will cancel the create/update and redirect the user back to the list view
     */
    async cancel(): Promise<void> {
        await this.router.navigateByUrl(`/administration/application/${this.parentApplicationId}`, { replaceUrl: true })
    }

    /**
     * Mark each FormControl as dirty and re-evaluate validity to establish
     * validation failures
     */
    async validateAll(): Promise<void> {
        Object.values(this.form.controls).forEach(control => {
            control.markAsDirty()
        })
        this.form.updateValueAndValidity()
    }

    /**
     * Sort the assigned/available groups in the picklist in alphabetical order
     *
     * @param groups List of assigned/available groups
     */
    sortGroups(groups: Group[]): void {
        groups.sort((group1, group2) => {
            // Use displayName if available, otherwise use groupId, or fallback to empty string
            const sortOn1 = group1.displayName ?? group1.groupId ?? ''
            const sortOn2 = group2.displayName ?? group2.groupId ?? ''

            return sortOn1.localeCompare(sortOn2)
        })
    }

    findNodeById(tree: TreeNode[], id: string | undefined): TreeNode | undefined {
        for (const node of tree) {
            if (node.data?.id == id) {
                return node
            }
            if (node.children?.length) {
                const foundNode = this.findNodeById(node.children, id)
                if (foundNode) {
                    return foundNode
                }
            }
        }
        return undefined
    }

    protected readonly ValidationUtils = ValidationUtils
}
